import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TableCell,
} from '@mui/material';
import SettingsInputSwitcher from '../../../../components/Settings/SettingsController/SettingsInputSwitcher';
import ResetSetting from '../../../ResetSettingButton';
import { Controls } from '../../../../components/controls/Controls';
import AppointmentTypeConfigurationModal from '../../AppointmentTypeSetting/AppointmentTypeConfigurationModal';
import TelusAuthCredentialsModal from './TelusAuthCredentialsModal';
import TelusOperatingHoursModal from './TelusOperatingHoursModal';
import TelusCalendarConfigurationModal from './TelusCalendarConfigurationModal';

export default function TelusSettingCell(props) {
  const {
    entity,
    setting,
    handleSave,
    setValue,
    refreshParent,
    setSuccessMessage,
    setErrorMessage,
    apiClient,
    selectedPlans,
    isAuthCredentialsSet,
  } = props;
  const [isAppointmentTypeModalOpen, setIsAppointmentTypeModalOpen] = useState(false);
  const [isAuthCredentialsModalOpen, setIsAuthCredentialsModalOpen] = useState(false);
  const [isOperatingHoursModalOpen, setIsOperatingHoursModalOpen] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [telusAppointmentTypes, setTelusAppointmentTypes] = useState([]);
  const [telusCalendars, setTelusCalendars] = useState([]);
  const [telusContacts, setTelusContacts] = useState([]);

  useEffect(() => {
    // Only make API calls if authCredentials is set
    if (!isAuthCredentialsSet) {
      return;
    }

    if (setting.name === 'telusAppointmentTypes' && apiClient && entity.id) {
      apiClient.getTelusAppointmentTypes(entity.id)
        .then((response) => {
          setTelusAppointmentTypes(response.data.appointmentTypes || []);
        })
        .catch(() => {
          setTelusAppointmentTypes([]);
        });
    }
    if (setting.name === 'telusCalendars' && apiClient && entity.id) {
      apiClient.getTelusCalendars(entity.id)
        .then((response) => {
          setTelusCalendars(response.data.calendars || []);
        })
        .catch(() => {
          setTelusCalendars([]);
        });
      apiClient.getTelusMessageContacts(entity.id)
        .then((response) => {
          setTelusContacts(response.data.contacts || []);
        })
        .catch(() => {
          setTelusContacts([]);
        });
    }
  }, [isAuthCredentialsSet]);

  const renderActionCell = () => {
    switch (setting.name) {
      case 'telusAppointmentTypes':
        return (
          <>
            <Controls.Button
              onClick={() => setIsAppointmentTypeModalOpen(true)}
              text="Edit"
            />
            <AppointmentTypeConfigurationModal
              isOpen={isAppointmentTypeModalOpen}
              handleClose={() => setIsAppointmentTypeModalOpen(false)}
              setting={setting}
              selectedPlans={selectedPlans}
              handleSave={handleSave}
              appointmentTypes={telusAppointmentTypes}
            />
          </>
        );
      case 'telusAuthCredentials':
        return (
          <>
            <Controls.Button
              onClick={() => setIsAuthCredentialsModalOpen(true)}
              text="Edit"
            />
            <TelusAuthCredentialsModal
              isOpen={isAuthCredentialsModalOpen}
              handleClose={() => setIsAuthCredentialsModalOpen(false)}
              setting={setting}
              handleSave={handleSave}
            />
          </>
        );
      case 'telusOperatingHours':
        return (
          <>
            <Controls.Button
              onClick={() => setIsOperatingHoursModalOpen(true)}
              text="Edit"
            />
            <TelusOperatingHoursModal
              isOpen={isOperatingHoursModalOpen}
              handleClose={() => setIsOperatingHoursModalOpen(false)}
              setting={setting}
              handleSave={handleSave}
            />
          </>
        );
      case 'telusCalendars':
        return (
          <>
            <Controls.Button
              onClick={() => setIsCalendarModalOpen(true)}
              text="Edit"
            />
            <TelusCalendarConfigurationModal
              isOpen={isCalendarModalOpen}
              handleClose={() => setIsCalendarModalOpen(false)}
              setting={setting}
              handleSave={handleSave}
              calendars={telusCalendars}
              contacts={telusContacts}
            />
          </>
        );
      default:
        return <div />;
    }
  };

  const renderValue = () => {
    switch (setting.name) {
      case 'telusAuthCredentials':
        return '••••••••••••';
      case 'telusAppointmentTypes':
        try {
          const appointmentTypes = setting.value ? JSON.parse(setting.value) : [];
          return (
            <>
              {appointmentTypes.map((appointmentType) => {
                const selectedPlan = appointmentType.intakePlanId
                  ? selectedPlans.find((plan) => plan.id === appointmentType.intakePlanId)
                  : null;
                return (
                  <div key={appointmentType.id}>
                    <b>{`${appointmentType.name}`}</b>
                    {' : '}
                    {selectedPlan ? `(${selectedPlan.referenceCode} : ${selectedPlan.moduleName})` : 'None'}
                  </div>
                );
              })}
            </>
          );
        } catch {
          return 'Not configured';
        }
      case 'telusOperatingHours':
        try {
          if (setting.value) {
            const hours = JSON.parse(setting.value);
            return `${hours.start} - ${hours.end}`;
          }
          return 'All day - 24 hours';
        } catch {
          return 'Error displaying';
        }
      case 'telusCalendars':
        try {
          const calendars = setting.value ? JSON.parse(setting.value) : [];
          if (calendars.length === 0) {
            return 'Not configured';
          }
          return (
            <>
              {calendars.map((calendar) => (
                <div key={calendar.id}>
                  <b>{calendar.name}</b>
                  {calendar.provider && (
                    <span>
                      {' - Provider: '}
                      {calendar.provider.userDetail.firstName}
                      {' '}
                      {calendar.provider.userDetail.lastName}
                    </span>
                  )}
                  {' - Notifications: '}
                  {calendar.sendNotification ? 'On' : 'Off'}
                </div>
              ))}
            </>
          );
        } catch {
          return 'Not configured';
        }
      default:
        return (
          <SettingsInputSwitcher
            type="text"
            setting={setting}
            setValues={setValue}
            refreshParent={refreshParent}
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
            contextType="site"
          />
        );
    }
  };

  return (
    <>
      <TableCell>
        {renderValue()}
      </TableCell>
      <TableCell>
        {renderActionCell()}
      </TableCell>
      <TableCell>
        <ResetSetting
          entity={entity}
          type="site"
          setting={setting}
          refreshParent={refreshParent}
          setErrorMessage={setErrorMessage}
          setSuccessMessage={setSuccessMessage}
          minimize
        />
      </TableCell>
    </>
  );
}

const planShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  referenceCode: PropTypes.string.isRequired,
  moduleName: PropTypes.string.isRequired,
});

TelusSettingCell.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    settings: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
      value: PropTypes.string,
    })),
  }).isRequired,
  setting: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  handleSave: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  refreshParent: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
  apiClient: PropTypes.shape({
    getTelusAppointmentTypes: PropTypes.func,
    getTelusCalendars: PropTypes.func,
    getTelusMessageContacts: PropTypes.func,
  }).isRequired,
  selectedPlans: PropTypes.arrayOf(planShape).isRequired,
  isAuthCredentialsSet: PropTypes.bool,
};

TelusSettingCell.defaultProps = {
  isAuthCredentialsSet: false,
};
